import React, { useCallback, useMemo, useState } from 'react';
import { Button, ColumnVisibilityMenu, DataTable, SearchInput, useDataTable } from '@onward-delivery/design';
import { Plus as PlusIcon } from '@onward-delivery/design/icons';

import { resourceDayState } from '@/utilities/resourceDayState';

import { fleetColumns } from './columns';
import { filterResources, subregionLabelsById } from './utils';
import StatCards from './StatCards';

// Every resource as a list. This is where one is created and configured — the calendar schedules
// what already exists, and creating from a day cell scoped the new resource to that day for no
// reason the operator ever asked for.
// Everything through props rather than the store's Context: importing that module pulls @/hooks,
// which reaches App.js and firebase/auth, and jsdom cannot load undici. Day.jsx is set up the same
// way for the same reason.
const Fleet = ({ resources = [], subregionsOrdered = [], today, onEdit }) => {
    const [search, setSearch] = useState('');
    // Controlled rather than an initial state, so the column menu can still turn it back on.
    const [columnVisibility, setColumnVisibility] = useState({ timeframe: false });

    const subregionLabels = useMemo(() => subregionLabelsById(subregionsOrdered), [subregionsOrdered]);

    // Scoped to today, since a resource in a list has no day. The dialog resolves the rule it is
    // editing from the day it was opened on, and today is the only defensible one off the calendar.
    // The entity is derived the same way a day cell derives it, so the dialog opens on the rule
    // that actually governs the resource right now.
    //
    // `?? undefined` because a resource that does not run today has no governing rule, and
    // scheduleResource() reports that as null — which is not what the dialog's `rule = {}` default
    // reads as absent. The grid lists every resource, scheduled today or not, so this is the common
    // case here in a way it never was on the calendar.
    const openResource = useCallback(
        (resource) =>
            onEdit([resource, resource ? resourceDayState(resource, today).entity ?? undefined : undefined, today]),
        [onEdit, today]
    );

    const columns = useMemo(
        () => fleetColumns({ subregionLabels, onEdit: openResource }),
        [subregionLabels, openResource]
    );

    const rows = useMemo(
        () => filterResources(resources, search, subregionLabels),
        [resources, search, subregionLabels]
    );

    // Pinned, not just last: the row is wider than the viewport, so the way into a resource's
    // configuration would otherwise sit off-screen until you scrolled to the end of it.
    const table = useDataTable({
        columns,
        data: rows,
        getRowId: (row) => row.resource_id,
        columnPinning: { right: ['edit'] },
        columnVisibility,
        onColumnVisibilityChange: setColumnVisibility,
    });

    return (
        <div className="onward-design flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
            <StatCards resources={resources} />

            {/* Composed rather than DataTableToolbar, matching Forward Stocking: its own search box
                only filters rows already in the browser and leaves a flex spacer when disabled. */}
            <div className="mb-4 flex shrink-0 flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
                <SearchInput
                    className="min-w-48 flex-1"
                    placeholder="Search name, vehicle, or subregion…"
                    value={search}
                    onValueChange={setSearch}
                />
                <span className="shrink-0 text-sm text-muted">
                    {table.getFilteredRowModel().rows.length.toLocaleString()} resources
                </span>
                <ColumnVisibilityMenu table={table} />
                {/* The search box's flex-1 is what pushes this to the right edge; ml-auto keeps it
                    there on the wrapped row at narrow widths. */}
                <Button className="ml-auto shrink-0" onClick={() => openResource(undefined)}>
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Create resource
                </Button>
            </div>

            {/* mb-24 keeps the grid's bottom edge above the HubSpot chat launcher (~60px tall, ~20px
                off the bottom-right corner), which otherwise sat on the pinned Edit buttons of the
                last rows. On the table, not the wrapper: .onward-design scoping drops utilities
                placed on the wrapper itself. */}
            <DataTable
                table={table}
                emptyState="No resources yet"
                className="mb-24 flex min-h-0 w-full min-w-0 flex-1 flex-col [&>div>table>thead]:sticky [&>div>table>thead]:top-0 [&>div>table>thead]:z-10"
            />
        </div>
    );
};

export default Fleet;
